import { useState, useEffect, useCallback } from 'react';
import type { AdminAuthState, AdminUser } from '../types/admin.types';

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

type UseAdminAuthResult = AdminAuthState & {
  login: (token: string, user: AdminUser) => void;
  logout: () => void;
};

export const useAdminAuth = (): UseAdminAuthResult => {
  const [authState, setAuthState] = useState<AdminAuthState>(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    const user = userJson ? (JSON.parse(userJson) as AdminUser) : null;

    return {
      isAuthenticated: !!token && !!user,
      token,
      user,
    };
  });

  const login = useCallback((token: string, user: AdminUser) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setAuthState({
      isAuthenticated: true,
      token,
      user,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setAuthState({
      isAuthenticated: false,
      token: null,
      user: null,
    });
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === TOKEN_KEY || e.key === USER_KEY) {
        const token = localStorage.getItem(TOKEN_KEY);
        const userJson = localStorage.getItem(USER_KEY);
        const user = userJson ? (JSON.parse(userJson) as AdminUser) : null;

        setAuthState({
          isAuthenticated: !!token && !!user,
          token,
          user,
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    ...authState,
    login,
    logout,
  };
};
